import { initializeApp, type FirebaseOptions } from 'firebase/app'
import { getAuth, connectAuthEmulator } from 'firebase/auth'
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore'
import { getStorage, connectStorageEmulator } from 'firebase/storage'
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions'
import { log } from '@/lib/logger'

/**
 * The Firebase web config is not a secret. It identifies the project to
 * Google's servers and ships in every client bundle by design. What keeps the
 * data safe is the Firestore and Storage rules, not hiding these six strings.
 * Real secrets — the Resend key, the TOTP encryption key, the deploy service
 * account — never reach the browser; they live in Secret Manager and are read
 * only inside Cloud Functions.
 */
const config: FirebaseOptions = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

const missing = Object.entries(config).filter(([, v]) => !v).map(([k]) => k)
if (missing.length) {
  log.error('firebase config incomplete', { missing })
}

export const REGION = 'asia-south1'

export const app = initializeApp(config)
export const auth = getAuth(app)
export const db = getFirestore(app)
export const storage = getStorage(app)
export const functions = getFunctions(app, REGION)

if (import.meta.env.VITE_USE_EMULATORS === 'true') {
  log.info('using local emulators')
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true })
  connectFirestoreEmulator(db, '127.0.0.1', 8080)
  connectStorageEmulator(storage, '127.0.0.1', 9199)
  connectFunctionsEmulator(functions, '127.0.0.1', 5001)
}
